import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Switch,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type {
  ConversationTriage,
  ConversationTriageState,
  CurrentTriage,
  GetTriageSettingsPageDataResponse,
  TriageTemplate,
} from '@provision/entity-types';
import {
  Ban,
  CircleDot,
  Clock,
  Gauge,
  Lightbulb,
  RotateCcw,
  Tag,
  UserRound,
  Users,
  Wrench,
  Zap,
} from 'lucide-react';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { omit } from 'remeda';
import { ExpandableTextArea } from 'src/components/ExpandableTextArea';
import { SelectMenu } from 'src/components/SelectMenu';
import { SelectMenuOption } from 'src/components/SelectMenu/select-menu.types';
import { SettingsSection, SettingsSectionRow } from 'src/components/SettingsSection';
// Direct file imports, not the aggregate `src/graphql` barrel or the
// per-entity folder barrels: importing the aggregate here pulls in the
// entire src/graphql module graph, which hits a pre-existing circular-init
// crash in address.ts (`gql` tag evaluates a not-yet-initialized constant) —
// reproduced independently of this feature's changes.
import {
  buildPlatformDefaultPlaceholder,
  parseTriageActionConfig,
  PLATFORM_DEFAULT_PLACEHOLDER_ID,
} from 'src/graphql/entities/conversation-triage/conversation-triage.mappings';
import { TriageActionConfigSnapshot } from 'src/graphql/entities/conversation-triage/conversation-triage.types';
import { useManageTriageConfigs } from 'src/graphql/entities/conversation-triage/hooks/use-manage-triage-configs';
import { useSaveConversationTriage } from 'src/graphql/entities/conversation-triage/hooks/use-save-conversation-triage';
import { useTriageSettingsPageData } from 'src/graphql/entities/conversation-triage/hooks/use-triage-settings-page-data';
import { TriageActionVisibility } from 'src/graphql/entities/conversation-triage-run/conversation-triage-run.types';
import {
  PartnerSetting,
  useManagePartnerSettingsValue,
  usePartnerSettings,
} from 'src/graphql/entities/partner-setting';
import { useGlobalStore } from 'src/store';
import { useConfirmationModal } from 'src/utils/hooks/use-confirmation-modal';
import { useDebounce } from 'src/utils/hooks/use-debounce';
import { useNotifications } from 'src/utils/hooks/use-notifications';
import { isStringBooleanTrue } from 'src/utils/strings';
import {
  TriageConfigSelector,
  type TriageConfigSelectorProps,
} from './triage-config-selector';

const useVisibilityOptions = (): SelectMenuOption[] => {
  const theme = useTheme();
  return [
    {
      value: TriageActionVisibility.Hide,
      title: 'Not allowed',
      description: 'Never runs — Triage skips this tool entirely.',
      icon: <Ban />,
      iconColor: theme.palette.custom.OV.sys.error,
    },
    {
      value: TriageActionVisibility.Show,
      title: 'Suggest',
      description: 'Recommends the action and leaves it for you to apply.',
      icon: <Lightbulb />,
      iconColor: theme.palette.custom.OV.sys.onSurfaceVariant,
    },
    {
      value: TriageActionVisibility.AutoApply,
      title: 'Auto-apply',
      description: 'Runs and applies the result automatically.',
      icon: <Zap />,
      iconColor: theme.palette.custom.OV.sys.secondary,
    },
  ];
};

const TOOL_ICONS: Record<string, ReactNode> = {
  change_urgency: <Gauge />,
  change_status: <CircleDot />,
  change_conversation_type: <Tag />,
  change_conversation_assignee_group: <Users />,
  snooze: <Clock />,
  set_requester: <UserRound />,
};

const INSTRUCTIONS_SAVE_DEBOUNCE_MS = 1000;
const INSTRUCTIONS_CONTROL = 'instructions';
const INSTRUCTIONS_RESET_CONTROL = 'instructions-reset';
const AUTOSAVE_ERROR_MESSAGE = 'Failed to save Triage Intelligence settings, please try again';

const resetInstructionsButtonSx: SxProps<Theme> = {
  minWidth: 0,
  px: 1.5,
  py: 0.375,
  borderRadius: '0.375rem',
  color: 'custom.OV.sys.onSurfaceVariant',
  fontSize: '0.71875rem',
  fontWeight: 600,
  textTransform: 'none',
  '& .MuiButton-startIcon': { mr: 0.625 },
  '&:hover': {
    color: 'custom.OV.sys.onBackground',
    backgroundColor: 'custom.OV.sys.surfaceContainerHigh',
  },
};

/**
 * Instructions + per-tool modes for one `ConversationTriage` record, including
 * the autosave chain. Remounted via `key={selectedConfigId}` by the parent so
 * switching records re-seeds every ref instead of leaking edits into the
 * previously loaded row.
 */
const TriageConfigEditor = ({
  partnerId,
  enabled,
  template,
  config,
  isFirstCreate,
  refetch,
}: {
  partnerId: number | null;
  enabled: boolean;
  template: TriageTemplate;
  config: ConversationTriageState | null;
  isFirstCreate: boolean;
  refetch: () => Promise<unknown>;
}) => {
  const visibilityOptions = useVisibilityOptions();
  const { saveConversationTriage } = useSaveConversationTriage(partnerId);
  const { showErrorAlert } = useNotifications();

  const [instructions, setInstructions] = useState(
    config?.instructions ?? template.instructions,
  );
  const [snapshot, setSnapshot] = useState<TriageActionConfigSnapshot>(() =>
    parseTriageActionConfig(config?.actionConfig ?? template.actionConfig),
  );
  const [pendingControls, setPendingControls] = useState<ReadonlySet<string>>(new Set());

  // Latest intended values, read at save time so a serialized save always
  // persists the freshest full config rather than a stale closure snapshot.
  const instructionsRef = useRef(instructions);
  const snapshotRef = useRef(snapshot);
  const savedInstructionsRef = useRef(instructions);
  const triageIdRef = useRef(config?.conversationTriageId ?? null);
  const llmConfigRef = useRef(config?.llmConfig ?? template.llmConfig);
  // Serializes saves so out-of-order completions can't overwrite newer config,
  // and the row id from the first create is reused instead of creating duplicates.
  const saveChainRef = useRef<Promise<void>>(Promise.resolve());
  const queuedSaveCountRef = useRef(0);
  const isPageDataStaleRef = useRef(false);

  const enqueueSave = useCallback(
    (control: string) => {
      setPendingControls((prev) => new Set(prev).add(control));
      queuedSaveCountRef.current += 1;
      const earlierSaves = saveChainRef.current;
      saveChainRef.current = (async () => {
        await earlierSaves;
        try {
          const isFirstCreateSave = triageIdRef.current === null && isFirstCreate;
          const saved = await saveConversationTriage({
            conversationTriageId: triageIdRef.current,
            instructions: instructionsRef.current,
            actionConfig: JSON.stringify(snapshotRef.current),
            llmConfig: llmConfigRef.current,
            ...(isFirstCreateSave && { isDefault: true }),
          });
          if (saved) {
            triageIdRef.current = saved.conversationTriageId;
            llmConfigRef.current = saved.llmConfig;
            savedInstructionsRef.current = saved.instructions;
            isPageDataStaleRef.current = true;
          }
          // Refreshes the settings query so the cached `configurations` list
          // (the editor's re-seed source on remount) doesn't go stale after
          // an autosave, which never otherwise touches the query cache.
          // Only the last save of a burst pays for it — the rest would refetch
          // a list the still-queued saves are about to change again.
          if (isPageDataStaleRef.current && queuedSaveCountRef.current === 1) {
            await refetch();
            isPageDataStaleRef.current = false;
          }
        } catch (error) {
          console.error(error);
          showErrorAlert(AUTOSAVE_ERROR_MESSAGE);
        } finally {
          queuedSaveCountRef.current -= 1;
          setPendingControls((prev) => {
            const next = new Set(prev);
            next.delete(control);
            return next;
          });
        }
      })();
    },
    [saveConversationTriage, isFirstCreate, refetch, showErrorAlert],
  );

  const saveInstructionsIfChanged = useCallback(
    (control: string) => {
      if (instructionsRef.current !== savedInstructionsRef.current) {
        enqueueSave(control);
      }
    },
    [enqueueSave],
  );

  const debouncedSaveInstructions = useDebounce(
    () => saveInstructionsIfChanged(INSTRUCTIONS_CONTROL),
    INSTRUCTIONS_SAVE_DEBOUNCE_MS,
  );

  // Flushes a still-pending debounced instructions save when the user
  // switches records mid-debounce (this component remounts on every
  // selection change), so the edit isn't dropped on the floor.
  useEffect(() => {
    return () => saveInstructionsIfChanged(INSTRUCTIONS_CONTROL);
  }, [saveInstructionsIfChanged]);

  const handleInstructionsChange = useCallback(
    (value: string) => {
      setInstructions(value);
      instructionsRef.current = value;
      debouncedSaveInstructions();
    },
    [debouncedSaveInstructions],
  );

  const handleInstructionsBlur = useCallback(() => {
    saveInstructionsIfChanged(INSTRUCTIONS_CONTROL);
  }, [saveInstructionsIfChanged]);

  const handleInstructionsReset = useCallback(() => {
    setInstructions(template.instructions);
    instructionsRef.current = template.instructions;
    saveInstructionsIfChanged(INSTRUCTIONS_RESET_CONTROL);
  }, [template.instructions, saveInstructionsIfChanged]);

  const handleVisibilityChange = useCallback(
    (toolName: string, visibility: string) => {
      const nextSnapshot = { ...snapshotRef.current, [toolName]: { visibility } };
      setSnapshot(nextSnapshot);
      snapshotRef.current = nextSnapshot;
      enqueueSave(toolName);
    },
    [enqueueSave],
  );

  return (
    <>
      <ExpandableTextArea
        title="Agent instructions"
        value={instructions}
        onChange={(e) => handleInstructionsChange(e.target.value)}
        onBlur={handleInstructionsBlur}
        disabled={!enabled}
        placeholder="Tell the agent how to triage conversations for this partner…"
        action={
          <Button
            variant="text"
            color="inherit"
            onClick={handleInstructionsReset}
            disabled={!enabled || pendingControls.has(INSTRUCTIONS_RESET_CONTROL)}
            startIcon={<RotateCcw size={13} />}
            sx={resetInstructionsButtonSx}
          >
            Reset to default
          </Button>
        }
      />

      <SettingsSection
        title="Behavior"
        description="Set how far Triage Intelligence can go for each tool call."
      >
        {template.tools.length === 0 ? (
          <Box px={2.25} py={2}>
            <Typography variant="body2" color="custom.OV.sys.onSurfaceVariant">
              No configurable tools are available.
            </Typography>
          </Box>
        ) : (
          template.tools.map((tool) => (
            <Box key={tool.name} data-testid={`triage-tool-row-${tool.name}`}>
              <SettingsSectionRow
                icon={TOOL_ICONS[tool.name] ?? <Wrench />}
                title={tool.label}
                secondary={tool.description}
                mobileControlLayout="below"
                control={
                  <SelectMenu
                    label={`${tool.label} permission`}
                    options={visibilityOptions}
                    value={snapshot[tool.name]?.visibility ?? template.defaultVisibility}
                    onChange={(value) => handleVisibilityChange(tool.name, value)}
                    disabled={!enabled || pendingControls.has(tool.name)}
                    sx={{ minWidth: '10rem' }}
                  />
                }
              />
            </Box>
          ))
        )}
      </SettingsSection>
    </>
  );
};

type TriageConfigActions = Pick<
  TriageConfigSelectorProps,
  | 'onSelect'
  | 'onCreate'
  | 'onDuplicate'
  | 'onRenameStart'
  | 'onRenameCommit'
  | 'onRenameCancel'
  | 'onSetDefault'
  | 'onDelete'
>;

// The placeholder id never names a real row, so it must never reach a
// mutation — the ⋮ actions are disabled in that state (defense in depth:
// never trust UI-only disabling for a real invariant).
const guardPlaceholderId =
  <Args extends unknown[]>(action: (id: string, ...args: Args) => void) =>
  (id: string, ...args: Args) => {
    if (id === PLATFORM_DEFAULT_PLACEHOLDER_ID) {
      return;
    }
    action(id, ...args);
  };

/**
 * Owns which configuration is selected or being renamed, and the create /
 * duplicate / rename / set-default / delete actions that mutate the list.
 */
const useTriageConfigActions = ({
  partnerId,
  template,
  currentTriage,
  refetch,
}: {
  partnerId: number | null;
  template: TriageTemplate;
  currentTriage: CurrentTriage;
  refetch: () => Promise<unknown>;
}) => {
  const { configurations, defaultConversationTriageId } = currentTriage;

  // Initial pick only — re-seeded on every partner switch because this whole
  // component is keyed by partnerId. Kept in sync with live data below.
  const [selectedConfigId, setSelectedConfigId] = useState<string | null>(
    () => defaultConversationTriageId ?? configurations[0]?.conversationTriageId ?? null,
  );
  const [renamingId, setRenamingId] = useState<string | null>(null);
  // A rename is a mutation plus a full refetch of the non-normalized page
  // query, so the committed name is not in `configurations` for the whole
  // round trip; without this overlay the trigger falls back to the
  // "Default configuration" placeholder in between.
  const [namesBeingSaved, setNamesBeingSaved] = useState<Record<string, string>>({});

  // Falls back to the default whenever the picked id no longer exists in the
  // live list (e.g. it was just soft-deleted) — derived so no effect is needed.
  const resolvedSelectedConfigId = useMemo(() => {
    if (
      selectedConfigId &&
      configurations.some((config) => config.conversationTriageId === selectedConfigId)
    ) {
      return selectedConfigId;
    }
    return defaultConversationTriageId ?? configurations[0]?.conversationTriageId ?? null;
  }, [selectedConfigId, configurations, defaultConversationTriageId]);

  const resolvedRenamingId =
    renamingId && configurations.some((config) => config.conversationTriageId === renamingId)
      ? renamingId
      : null;

  const configurationsWithSavedNames = useMemo(
    () =>
      configurations.map((config) =>
        config.conversationTriageId in namesBeingSaved
          ? { ...config, name: namesBeingSaved[config.conversationTriageId] }
          : config,
      ),
    [configurations, namesBeingSaved],
  );

  const {
    createFromDefaults,
    duplicateConfig,
    renameConfig,
    setDefaultConfig,
    softDeleteConfig,
  } = useManageTriageConfigs({
    partnerId,
    template,
    hasConfigurations: configurations.length > 0,
    refetch,
  });

  const { confirm } = useConfirmationModal();

  const selectAndEnterRename = useCallback((created: ConversationTriage | null) => {
    if (created) {
      setSelectedConfigId(created.conversationTriageId);
      setRenamingId(created.conversationTriageId);
    }
  }, []);

  const createFromDefaultsAndSelect = useCallback(async () => {
    selectAndEnterRename(await createFromDefaults());
  }, [createFromDefaults, selectAndEnterRename]);

  const duplicateAndSelect = useCallback(
    async (sourceId: string) => {
      const source = configurations.find((config) => config.conversationTriageId === sourceId);
      if (!source) {
        return;
      }
      selectAndEnterRename(await duplicateConfig(source));
    },
    [configurations, duplicateConfig, selectAndEnterRename],
  );

  const renameAndShowNameImmediately = useCallback(
    async (id: string, name: string) => {
      setNamesBeingSaved((current) => ({ ...current, [id]: name }));
      try {
        await renameConfig(id, name);
      } finally {
        // Dropped only if this rename is still the latest one for the row, so a
        // second rename started mid-flight keeps its own optimistic name. On
        // failure the refetched record wins and the name reverts.
        setNamesBeingSaved((current) =>
          current[id] === name ? omit(current, [id]) : current,
        );
      }
    },
    [renameConfig],
  );

  const confirmAndDelete = useCallback(
    async (id: string) => {
      const config = configurations.find((candidate) => candidate.conversationTriageId === id);
      const confirmed = await confirm({
        title: 'Delete configuration?',
        body: `"${config?.name ?? 'This configuration'}" will be deleted permanently.`,
        confirmLabel: 'Delete configuration',
        danger: true,
      });
      if (confirmed) {
        await softDeleteConfig(id);
      }
    },
    [configurations, confirm, softDeleteConfig],
  );

  const actions = useMemo<TriageConfigActions>(
    () => ({
      onSelect: (id: string) => {
        setSelectedConfigId(id);
        setRenamingId(null);
      },
      // Mirrors the hidden create row while zero rows exist: the lazy
      // first-create autosave already claims `isDefault`, so a create racing it
      // would insert a second default and violate the per-partner unique index.
      onCreate: () => {
        if (configurations.length === 0) {
          return;
        }
        void createFromDefaultsAndSelect();
      },
      onRenameCancel: () => {
        setRenamingId(null);
      },
      onDuplicate: guardPlaceholderId((id: string) => {
        void duplicateAndSelect(id);
      }),
      onRenameStart: guardPlaceholderId((id: string) => {
        setRenamingId(id);
      }),
      onRenameCommit: guardPlaceholderId((id: string, name: string) => {
        setRenamingId(null);
        void renameAndShowNameImmediately(id, name);
      }),
      onSetDefault: guardPlaceholderId((id: string) => {
        void setDefaultConfig(id);
      }),
      onDelete: guardPlaceholderId((id: string) => {
        void confirmAndDelete(id);
      }),
    }),
    [
      configurations.length,
      createFromDefaultsAndSelect,
      duplicateAndSelect,
      renameAndShowNameImmediately,
      setDefaultConfig,
      confirmAndDelete,
    ],
  );

  return {
    configurations: configurationsWithSavedNames,
    selectedConfigId: resolvedSelectedConfigId,
    renamingId: resolvedRenamingId,
    actions,
  };
};

const AiPreferencesContent = ({
  partnerId,
  data,
  refetch,
}: {
  partnerId: number | null;
  data: GetTriageSettingsPageDataResponse;
  refetch: () => Promise<unknown>;
}) => {
  const { template, currentTriage } = data;

  const { data: enabledSettingRecords } = usePartnerSettings({
    partnerId,
    partnerSetting: PartnerSetting.ConversationTriageEnabled,
  });
  const enabledSetting = Array.isArray(enabledSettingRecords)
    ? enabledSettingRecords.find((record) => record.partnerId === partnerId)
    : undefined;
  const { updatePartnerSettingsValue, createPartnerSettingsValue } =
    useManagePartnerSettingsValue(partnerId);

  const enabled =
    enabledSetting?.value !== undefined
      ? isStringBooleanTrue(enabledSetting.value)
      : currentTriage.enabled;
  const [isTogglingEnabled, setIsTogglingEnabled] = useState(false);

  const handleEnabledToggle = useCallback(
    async (checked: boolean) => {
      setIsTogglingEnabled(true);
      try {
        if (enabledSetting?.partnerSettingValueId) {
          await updatePartnerSettingsValue({
            partnerSettingValueId: enabledSetting.partnerSettingValueId,
            value: String(checked),
            partnerSettingId: PartnerSetting.ConversationTriageEnabled,
          });
        } else {
          await createPartnerSettingsValue({
            value: String(checked),
            partnerSettingId: PartnerSetting.ConversationTriageEnabled,
          });
        }
      } finally {
        setIsTogglingEnabled(false);
      }
    },
    [enabledSetting, updatePartnerSettingsValue, createPartnerSettingsValue],
  );

  const {
    configurations,
    selectedConfigId: resolvedSelectedConfigId,
    renamingId: resolvedRenamingId,
    actions,
  } = useTriageConfigActions({ partnerId, template, currentTriage, refetch });

  const selectedConfig =
    configurations.find(
      (config) => config.conversationTriageId === resolvedSelectedConfigId,
    ) ?? null;

  // Zero real rows: the selector still renders, showing what the runtime
  // would actually use — a single display-only entry built from `template`
  // (never persisted, never a valid mutation target). The first edit lazily
  // creates the partner's real row via `isFirstCreate` below, and a refetch
  // replaces this placeholder with that record.
  const hasConfigurations = configurations.length > 0;
  const displayConfigurations = hasConfigurations
    ? configurations
    : [buildPlatformDefaultPlaceholder(template)];
  const displaySelectedId = hasConfigurations
    ? resolvedSelectedConfigId
    : PLATFORM_DEFAULT_PLACEHOLDER_ID;
  const displayDefaultId = hasConfigurations
    ? currentTriage.defaultConversationTriageId
    : PLATFORM_DEFAULT_PLACEHOLDER_ID;

  return (
    <Stack spacing={2}>
      <SettingsSection
        title="Triage Intelligence"
        description="Automatically triages new conversations — inferring properties and routing them to the right place."
      >
        <SettingsSectionRow
          title="Enable Triage Intelligence"
          secondary="Let Triage Intelligence read, classify and route new conversations."
          control={
            <Switch
              checked={enabled}
              disabled={isTogglingEnabled}
              onChange={(e) => void handleEnabledToggle(e.target.checked)}
              inputProps={{ 'aria-label': 'Enable Triage Intelligence' }}
            />
          }
        />
      </SettingsSection>

      <Stack
        spacing={2.5}
        sx={{
          opacity: enabled ? 1 : 0.4,
          pointerEvents: enabled ? 'auto' : 'none',
          transition: (theme) => theme.transitions.create('opacity'),
        }}
        aria-disabled={!enabled}
      >
        <TriageConfigSelector
          configurations={displayConfigurations}
          selectedId={displaySelectedId}
          defaultId={displayDefaultId}
          renamingId={resolvedRenamingId}
          tools={template.tools}
          defaultVisibility={template.defaultVisibility}
          disabled={!enabled}
          actionsDisabled={!hasConfigurations}
          {...actions}
        />

        <TriageConfigEditor
          key={resolvedSelectedConfigId ?? 'new'}
          partnerId={partnerId}
          enabled={enabled}
          template={template}
          config={selectedConfig}
          isFirstCreate={!hasConfigurations}
          refetch={refetch}
        />
      </Stack>
    </Stack>
  );
};

export const AiPreferences = () => {
  const partnerId = useGlobalStore.use.selectedPartnerId();
  const { data, isLoading, error, refetch } = useTriageSettingsPageData(partnerId);

  if (isLoading) {
    return (
      <Stack alignItems="center" py={6}>
        <CircularProgress size={28} />
      </Stack>
    );
  }

  if (error || !data) {
    return (
      <Stack alignItems="flex-start" spacing={2} py={2}>
        <Typography variant="body2" color="text.secondary">
          Triage Intelligence settings are unavailable right now.
        </Typography>
        <Button variant="outlined" size="small" onClick={() => void refetch()}>
          Retry
        </Button>
      </Stack>
    );
  }

  return (
    <AiPreferencesContent
      key={partnerId}
      partnerId={partnerId}
      data={data}
      refetch={refetch}
    />
  );
};
